import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';


import { gnnApi, GraphStats, PredictionResponse } from '../services/api';
import { ModelSelector } from '../components/ModelSelector';
import { PredictionCard } from '../components/PredictionCard';
import { ClassDistribution } from '../components/ClassDistribution';
import { AnimatedCounter } from '../components/AnimatedCounter';
import { PageTransition } from '../components/PageTransition';

const containerVariants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0, transition: { duration: 0.4 } }
};

export const Dashboard: React.FC = () => {
  const [stats, setStats] = useState<GraphStats | null>(null);
  const [selectedModel, setSelectedModel] = useState('gcn');
  const [predictions, setPredictions] = useState<PredictionResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [nodeInput, setNodeInput] = useState('');
  const [toast, setToast] = useState<{ type: 'error' | 'success'; message: string } | null>(null);

  useEffect(() => {
    loadStatistics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const showToast = (type: 'error' | 'success', message: string) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 4000);
  };

  const loadStatistics = async () => {
    try {
      const response = await gnnApi.getStatistics();
      setStats(response.data);
    } catch (error) {
      showToast('error', 'Failed to load graph statistics');
    }
  };

  const handleRandomPredictions = async () => {
    setLoading(true);
    try {
      const response = await gnnApi.predictRandom(5, selectedModel);
      setPredictions(response.data);
      showToast('success', `Got predictions from ${selectedModel.toUpperCase()}`);
    } catch (error) {
      showToast('error', 'Failed to get predictions. Is the model trained?');
    } finally {
      setLoading(false);
    }
  };

  const handleCustomPrediction = async () => {
    if (!nodeInput.trim()) return;

    const indices = nodeInput
      .split(',')
      .map(s => parseInt(s.trim()))
      .filter(n => !isNaN(n));

    if (indices.length === 0) {
      showToast('error', 'Please enter valid node IDs');
      return;
    }

    setLoading(true);
    try {
      const response = await gnnApi.predict({
        node_indices: indices,
        model_type: selectedModel as any
      });
      setPredictions(response.data);
      showToast('success', `Classified ${indices.length} paper(s)`);
    } catch (error) {
      showToast('error', 'Prediction failed. Check node IDs are valid (0-2707).');
    } finally {
      setLoading(false);
    }
  };

  const getAccuracyByModel = (model: string) => {
    switch (model) {
      case 'gat': return 81.5;
      case 'gcn': return 79.7;
      case 'graphsage': return 76.8;
      case 'mlp': return 42.8;
      default: return 0;
    }
  };

  return (
    <PageTransition className="dashboard">
      {/* Hero Header */}
      <motion.div 
        className="page-hero relative overflow-hidden rounded-2xl p-8 mb-8 border border-border-subtle"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-[rgba(0,251,251,0.15)] to-transparent pointer-events-none" />
        <div className="relative z-10">
          <h1 className="text-4xl md:text-5xl font-extrabold mb-3" style={{ color: '#6bf7fb' }}>
            GNN Citation Classifier
          </h1>
          <p className="text-text-secondary text-lg max-w-2xl leading-relaxed">
            State-of-the-art Graph Neural Networks applied to the Cora citation dataset. 
            Analyze document relationships and predict research topics with high precision.
          </p>
        </div>
        
        {/* Abstract shapes for background flavor */}
        <div className="absolute top-[-50%] right-[-10%] w-[300px] h-[300px] bg-accent-blue rounded-full blur-[100px] opacity-20 pointer-events-none" />
        <div className="absolute bottom-[-50%] right-[10%] w-[200px] h-[200px] bg-accent-violet rounded-full blur-[80px] opacity-20 pointer-events-none" />
      </motion.div>

      {/* Stats Grid */}
      {stats ? (
        <motion.div 
          className="stats-grid"
          variants={containerVariants}
          initial="hidden"
          animate="show"
        >
          <motion.div variants={itemVariants} className="stat-card">
            <div className="stat-icon text-accent-blue"><span className="icon-fallback" >📄</span></div>
            <h4>Papers (Nodes)</h4>
            <p className="stat-value text-accent-blue font-mono text-3xl">
              <AnimatedCounter value={stats.num_nodes} />
            </p>
          </motion.div>
          <motion.div variants={itemVariants} className="stat-card">
            <div className="stat-icon text-accent-cyan"><span className="icon-fallback" >🔗</span></div>
            <h4>Citations (Edges)</h4>
            <p className="stat-value text-accent-cyan font-mono text-3xl">
              <AnimatedCounter value={stats.num_edges} />
            </p>
          </motion.div>
          <motion.div variants={itemVariants} className="stat-card">
            <div className="stat-icon text-accent-emerald"><span className="icon-fallback" >🧩</span></div>
            <h4>Features</h4>
            <p className="stat-value text-accent-emerald font-mono text-3xl">
              <AnimatedCounter value={stats.num_features} />
            </p>
          </motion.div>
          <motion.div variants={itemVariants} className="stat-card">
            <div className="stat-icon text-accent-violet"><span className="icon-fallback" >🏷️</span></div>
            <h4>Classes</h4>
            <p className="stat-value text-accent-violet font-mono text-3xl">
              <AnimatedCounter value={stats.num_classes} />
            </p>
          </motion.div>
        </motion.div>
      ) : (
        <div className="stats-grid">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="skeleton skeleton-stat rounded-2xl" />
          ))}
        </div>
      )}

      {/* Main Content Area */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 mb-8">
        {/* Left Column - Controls & Distribution */}
        <div className="xl:col-span-1 flex flex-col gap-6">
          <motion.div 
            className="glass-card"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 }}
          >
            <ModelSelector
              selectedModel={selectedModel}
              onModelChange={setSelectedModel}
            />

            <div className="mt-6 pt-6 border-t border-border-subtle">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-semibold text-text-primary">Expected Accuracy</h4>
                <div className="text-success font-mono font-bold">
                  {getAccuracyByModel(selectedModel)}%
                </div>
              </div>
              <div className="h-2 w-full bg-[rgba(255,255,255,0.05)] rounded-full overflow-hidden">
                <motion.div 
                  className="h-full bg-accent-gradient"
                  initial={{ width: 0 }}
                  animate={{ width: `${getAccuracyByModel(selectedModel)}%` }}
                  transition={{ duration: 1, ease: 'easeOut' }}
                />
              </div>
            </div>

            <div className="prediction-controls mt-6">
              <h3 className="flex items-center gap-2 text-lg font-bold mb-4">
                <span className="icon-fallback text-accent-amber" >⚡</span>
                Inference Engine
              </h3>

              <div className="input-group">
                <label>Target Nodes (comma-separated):</label>
                <div className="relative">
                  <input
                    type="text"
                    value={nodeInput}
                    onChange={(e) => setNodeInput(e.target.value)}
                    placeholder="e.g., 0, 100, 500"
                    onKeyDown={(e) => e.key === 'Enter' && handleCustomPrediction()}
                    className="w-full bg-[rgba(14,14,14,0.5)] border border-border-subtle focus:border-accent-blue rounded-lg px-4 py-3 outline-none text-text-primary font-mono text-sm transition-all shadow-inner"
                  />
                  <button
                    className="absolute right-2 top-1/2 -translate-y-1/2 px-3 py-1.5 bg-accent-blue hover:bg-accent-violet rounded-md text-white text-xs font-semibold transition-colors"
                    onClick={handleCustomPrediction}
                    disabled={loading || !nodeInput.trim()}
                  >
                    Run
                  </button>
                </div>
              </div>

              <div className="flex items-center my-4">
                <div className="flex-1 h-px bg-border-subtle"></div>
                <span className="px-3 text-xs text-text-muted font-medium uppercase">Or randomly sample</span>
                <div className="flex-1 h-px bg-border-subtle"></div>
              </div>

              <button
                className="w-full relative overflow-hidden rounded-lg p-[1px] group"
                onClick={handleRandomPredictions}
                disabled={loading}
              >
                <div className="absolute inset-0 bg-accent-gradient opacity-70 group-hover:opacity-100 transition-opacity" />
                <div className="relative bg-surface rounded-md px-4 py-3 flex items-center justify-center gap-2 group-hover:bg-opacity-0 transition-all font-semibold text-text-primary group-hover:text-white">
                  {loading ? (
                    <>
                      <span className="spinner w-4 h-4 border-2"></span>
                      <span>Processing Graph...</span>
                    </>
                  ) : (
                    <>
                      <span>🎲</span> 
                      <span>Classify 5 Random Papers</span>
                    </>
                  )}
                </div>
              </button>
            </div>
          </motion.div>

          <ClassDistribution />
        </div>

        {/* Right Column - Results */}
        <div className="xl:col-span-2">
          <motion.div 
            className="glass-card h-full flex flex-col"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4 }}
          >
            <div className="flex items-center justify-between mb-6 pb-4 border-b border-border-subtle">
              <h3 className="text-xl font-bold flex items-center gap-2 m-0">
                <span className="text-accent-emerald">🔬</span> 
                Classification Results
              </h3>
              
              {predictions && (
                <div className="flex gap-4 text-sm bg-[rgba(14,14,14,0.5)] px-4 py-2 rounded-lg">
                  <div className="flex items-center gap-2">
                    <span className="text-text-muted">Model:</span>
                    <span className="font-mono text-accent-cyan font-bold">{predictions.model_used.toUpperCase()}</span>
                  </div>
                  <div className="w-px bg-border-subtle" />
                  <div className="flex items-center gap-2">
                    <span className="text-text-muted">Time:</span>
                    <span className="font-mono text-accent-amber font-bold">{predictions.inference_time_ms}ms</span>
                  </div>
                </div>
              )}
            </div>

            <div className="flex-1 relative min-h-[400px]">
              {loading && !predictions ? (
                <div className="absolute inset-0 flex flex-col items-center justify-center z-10 bg-[rgba(32,31,31,0.8)] backdrop-blur-sm rounded-xl">
                  <div className="w-16 h-16 border-4 border-border-subtle border-t-accent-blue rounded-full animate-spin mb-4" />
                  <p className="text-accent-blue font-semibold animate-pulse tracking-wide">Propagating Node Features...</p>
                </div>
              ) : null}

              {!loading && !predictions && (
                <div className="absolute inset-0 flex flex-col items-center justify-center text-text-muted border-2 border-dashed border-border-subtle rounded-xl bg-[rgba(255,255,255,0.02)]">
                  <div className="text-5xl opacity-40 mb-4">🧪</div>
                  <p className="font-medium text-lg">Awaiting Input Data</p>
                  <p className="text-sm opacity-80 mt-2">Select a model and run predictions on the left panel</p>
                </div>
              )}

              {predictions && (
                <motion.div 
                  className="grid grid-cols-1 md:grid-cols-2 gap-4"
                  variants={containerVariants}
                  initial="hidden"
                  animate="show"
                >
                  {predictions.predictions.map((pred) => (
                    <motion.div key={pred.node_id} variants={itemVariants}>
                      <PredictionCard prediction={pred} />
                    </motion.div>
                  ))}
                </motion.div>
              )}
            </div>
          </motion.div>
        </div>
      </div>

      {/* Toast Notification */}
      <AnimatePresence>
        {toast && (
          <motion.div 
            className="fixed bottom-6 right-6 z-50 flex flex-col gap-2"
            initial={{ opacity: 0, y: 50, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.9 }}
          >
            <div className={`
              px-5 py-4 rounded-xl shadow-lg border backdrop-blur-md flex items-center gap-3 font-medium cursor-pointer
              ${toast.type === 'error' 
                ? 'bg-[rgba(255,180,171,0.1)] border-[rgba(255,180,171,0.3)] text-rose-300' 
                : 'bg-[rgba(107,247,251,0.1)] border-[rgba(107,247,251,0.3)] text-emerald-300'
              }
            `}
            onClick={() => setToast(null)}
            >
              <span className="text-xl">{toast.type === 'error' ? '❌' : '✅'}</span>
              {toast.message}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </PageTransition>
  );
};
